<script lang="ts">
  import { app } from "../../state/app.svelte";
  import * as conversations from "../../db/conversations";
  import { i18n } from "../../i18n/i18n.svelte";

  const fr = {
    deleteOne: (title: string) => `Supprimer la conversation "${title}" ?`,
    deleteMany: (n: number) => `Supprimer ${n} conversation${n > 1 ? "s" : ""} ?`,
    empty: "Aucune conversation passée.",
    selectAll: "Tout sélectionner",
    deselectAll: "Tout désélectionner",
    deleteSelection: (n: number) => `Supprimer la sélection${n > 0 ? ` (${n})` : ""}`,
    select: "Sélectionner",
    renameInput: "Renommer la conversation",
    rename: "Renommer",
    pin: "Épingler en haut",
    unpin: "Désépingler",
    delete: "Supprimer",
    docs: (n: number) => `${n} doc${n > 1 ? "s" : ""}`,
  };
  const en: typeof fr = {
    deleteOne: (title) => `Delete the conversation "${title}"?`,
    deleteMany: (n) => `Delete ${n} conversation${n > 1 ? "s" : ""}?`,
    empty: "No past conversations.",
    selectAll: "Select all",
    deselectAll: "Deselect all",
    deleteSelection: (n) => `Delete selection${n > 0 ? ` (${n})` : ""}`,
    select: "Select",
    renameInput: "Rename the conversation",
    rename: "Rename",
    pin: "Pin to top",
    unpin: "Unpin",
    delete: "Delete",
    docs: (n) => `${n} doc${n > 1 ? "s" : ""}`,
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  // Local-only selection state, used solely for the bulk-delete UI.
  let selected = $state<Set<string>>(new Set());

  // In-place rename state: which conversation is being renamed and the
  // current draft title. Null when nothing is being edited.
  let editingId = $state<string | null>(null);
  let editingTitle = $state("");

  async function open(id: string) {
    if (editingId) return; // ignore row-open while typing in the rename input
    await app.loadConversation(id);
  }

  function startRename(id: string, currentTitle: string) {
    editingId = id;
    editingTitle = currentTitle;
  }

  function cancelRename() {
    editingId = null;
    editingTitle = "";
  }

  async function commitRename() {
    if (!editingId) return;
    const id = editingId;
    const next = editingTitle.trim();
    editingId = null;
    editingTitle = "";
    if (!next) return; // empty title → keep the existing one
    const conv = await conversations.getConversation(id);
    if (!conv || conv.title === next) return;
    conv.title = next;
    // Keep updatedAt untouched on rename so the list ordering doesn't jump
    // around just because the user fixed a typo.
    await conversations.putConversation(conv);
    await app.refreshConversations();
  }

  function onRenameKey(e: KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      void commitRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelRename();
    }
  }

  function autofocusInput(node: HTMLInputElement) {
    node.focus();
    node.select();
  }

  async function togglePin(id: string, pinned: boolean) {
    await conversations.setConversationPinned(id, pinned);
    await app.refreshConversations();
  }

  function toggle(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selected = next;
  }

  function toggleSelectAll() {
    if (selected.size === app.conversations.length) {
      selected = new Set();
    } else {
      selected = new Set(app.conversations.map((c) => c.id));
    }
  }

  async function onDelete(id: string, title: string) {
    if (!confirm(t.deleteOne(title))) return;
    await deleteIds([id]);
  }

  async function onDeleteSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!confirm(t.deleteMany(ids.length))) return;
    await deleteIds(ids);
  }

  async function deleteIds(ids: string[]) {
    for (const id of ids) {
      await conversations.deleteConversation(id);
      if (app.currentConversationId === id) {
        app.currentConversationId = null;
        app.currentMessages = [];
      }
      const next = new Set(selected);
      next.delete(id);
      selected = next;
    }
    await app.refreshConversations();
  }

  function formatDate(ts: number): string {
    const d = new Date(ts);
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (sameDay) {
      return d.toLocaleTimeString(i18n.locale, { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString(i18n.locale, { day: "numeric", month: "short" });
  }
</script>

<section class="history">
  {#if app.conversations.length === 0}
    <p class="empty">{t.empty}</p>
  {:else}
    <div class="bulk">
      <button type="button" class="bulk-btn" onclick={toggleSelectAll}>
        {selected.size === app.conversations.length ? t.deselectAll : t.selectAll}
      </button>
      <button
        type="button"
        class="bulk-btn danger"
        onclick={onDeleteSelected}
        disabled={selected.size === 0}
      >{t.deleteSelection(selected.size)}</button>
    </div>
    <ul>
      {#each app.conversations as conv (conv.id)}
        <li class:current={conv.id === app.currentConversationId} class:pinned={conv.pinned}>
          <input
            type="checkbox"
            class="select"
            checked={selected.has(conv.id)}
            onchange={() => toggle(conv.id)}
            aria-label={t.select}
          />
          {#if editingId === conv.id}
            <div class="open editing">
              <input
                type="text"
                class="rename-input"
                bind:value={editingTitle}
                onkeydown={onRenameKey}
                onblur={commitRename}
                use:autofocusInput
                aria-label={t.renameInput}
              />
              <div class="meta">
                {t.docs(conv.docIds.length)} · {formatDate(conv.updatedAt)}
              </div>
            </div>
          {:else}
            <!-- The row truncates long titles to one line; the tooltip gives the
                 whole title back on hover. (Double-click still renames.) -->
            <button
              class="open"
              onclick={() => open(conv.id)}
              ondblclick={() => startRename(conv.id, conv.title)}
              title={conv.title}
            >
              <div class="title">{conv.title}</div>
              <div class="meta">
                {t.docs(conv.docIds.length)} · {formatDate(conv.updatedAt)}
              </div>
            </button>
            <button
              class="pin"
              class:on={conv.pinned}
              onclick={() => togglePin(conv.id, !conv.pinned)}
              title={conv.pinned ? t.unpin : t.pin}
              aria-label={conv.pinned ? t.unpin : t.pin}
              aria-pressed={!!conv.pinned}
            >📌</button>
            <button
              class="rename"
              onclick={() => startRename(conv.id, conv.title)}
              title={t.rename}
              aria-label={t.rename}
            >✎</button>
            <button
              class="delete"
              onclick={() => onDelete(conv.id, conv.title)}
              title={t.delete}
              aria-label={t.delete}
            >×</button>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .history {
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .bulk {
    display: flex;
    gap: 0.4rem;
  }

  .bulk-btn {
    padding: 0.3rem 0.6rem;
    background: var(--epfl-gray-50);
    color: var(--epfl-gray-600);
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    font-size: 0.75rem;
    font-family: inherit;
    cursor: pointer;
  }

  .bulk-btn:hover:not(:disabled) {
    background: var(--epfl-gray-100);
  }

  .bulk-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .bulk-btn.danger:not(:disabled) {
    color: var(--epfl-danger-text);
    border-color: var(--epfl-danger-border);
  }

  .bulk-btn.danger:hover:not(:disabled) {
    background: var(--epfl-danger-bg);
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  li {
    display: flex;
    align-items: stretch;
    background: var(--epfl-white);
    border: 1px solid var(--epfl-gray-100);
    border-radius: var(--epfl-radius);
  }

  li.current {
    border-color: var(--epfl-accent);
    background: var(--epfl-gray-50);
  }

  .select {
    margin: 0 0.4rem 0 0.6rem;
    align-self: center;
    cursor: pointer;
  }

  .open {
    flex: 1;
    text-align: left;
    background: none;
    border: none;
    padding: 0.55rem 0.7rem;
    cursor: pointer;
    font-family: inherit;
  }

  .title {
    font-size: 0.85rem;
    color: var(--epfl-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .meta {
    font-size: 0.7rem;
    color: var(--epfl-gray-400);
    margin-top: 0.15rem;
  }

  .pin,
  .rename,
  .delete {
    background: none;
    border: none;
    color: var(--epfl-gray-400);
    cursor: pointer;
    padding: 0 0.55rem;
  }

  .rename {
    font-size: 0.85rem;
  }

  /* Discrète tant qu'elle n'est pas épinglée, pleine une fois en tête. */
  .pin {
    font-size: 0.8rem;
    filter: grayscale(1);
    opacity: 0.35;
  }

  .pin:hover {
    opacity: 0.8;
  }

  .pin.on {
    filter: none;
    opacity: 1;
  }

  li.pinned {
    border-left: 3px solid var(--epfl-accent);
  }

  .delete {
    font-size: 1rem;
  }

  .rename:hover {
    color: var(--epfl-accent);
  }

  .delete:hover {
    color: var(--epfl-danger);
  }

  .open.editing {
    flex: 1;
    padding: 0.45rem 0.7rem;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .rename-input {
    font-family: inherit;
    font-size: 0.85rem;
    color: var(--epfl-text);
    padding: 0.25rem 0.4rem;
    border: 1px solid var(--epfl-accent);
    border-radius: var(--epfl-radius);
    outline: none;
    width: 100%;
    box-sizing: border-box;
    background: var(--epfl-white);
  }

  .rename-input:focus {
    box-shadow: 0 0 0 2px rgba(0, 116, 128, 0.2);
  }

  .empty {
    font-size: 0.85rem;
    color: var(--epfl-gray-400);
    text-align: center;
    padding: 1.5rem 0.5rem;
  }
</style>
